import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { CartConfigService } from '@spartacus/cart/base/core';
import {
  CART_NORMALIZER,
  Cart,
  OrderEntry,
  PromotionLocation,
  SelectiveCartFacade,
} from '@spartacus/cart/base/root';
import { AuthService, ConverterService, RoutingService, User } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { GenActiveCartService } from '../core/facade/active-cart.service';
import { GenUserGroup } from 'src/app/generac/enums/user.enum';
import { EntryGroup } from 'src/app/generac/models/cart.model';
import { GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';

@Component({
    selector: 'gen-cart-details',
    templateUrl: './cart-details.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GenCartDetailsComponent implements OnInit, OnDestroy {
  cart$: Observable<Cart>;
  entries$: Observable<EntryGroup[]>;
  cartLoaded$: Observable<boolean>;
  loggedIn = false;
  promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;
  selectiveCartEnabled: boolean;
  user: User;
  isNetRestrictedPriceGroup$!: Observable<boolean>;
  cart: Cart;

  private subscriptions = new Subscription();

  constructor(
    protected activeCartService: GenActiveCartService,
    protected selectiveCartService: SelectiveCartFacade,
    protected authService: AuthService,
    protected routingService: RoutingService,
    protected cartConfig: CartConfigService,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
    protected converterService: ConverterService,
    private userAccount: UserAccountFacade,
  ) { }

  ngOnInit() {
    this.cart$ = this.activeCartService.getActive() as Observable<Cart>;
    this.createEntryGroupsStream();
    this.selectiveCartEnabled = this.cartConfig.isSelectiveCartEnabled();
    this.createCartStream();
    this.getUserAccount();
    this.sendGAViewCartEvent();
  }

  saveForLater(item: OrderEntry) {
    if (this.loggedIn) {
      this.activeCartService.removeEntry(item);
      this.selectiveCartService.addEntry(
        item.product?.code ?? '',
        item.quantity ?? 0
      );
    } else {
      this.routingService.go({ cxRoute: 'login' });
    }
  }

  private createEntryGroupsStream() {
    this.entries$ = this.activeCartService
      .getActive()
      .pipe(
        this.converterService.pipeable<Cart, Cart>(CART_NORMALIZER),
        tap(cart => this.cart = cart),
        map((cart: any) => cart.entryGroups),
        filter((entryGroups) => entryGroups?.length > 0)
      )
  }

  private createCartStream() {
    this.cartLoaded$ = combineLatest([
      this.userAccount.get(),
      this.activeCartService.isStable(),
      this.selectiveCartEnabled
        ? this.selectiveCartService.isStable()
        : of(false),
      this.authService.isUserLoggedIn(),
    ]).pipe(
      tap(([user, , , loggedIn]) => {
        this.loggedIn = loggedIn;
        this.user = user;
      }),
      map(([, cartLoaded, sflLoaded, loggedIn]) =>
        loggedIn && this.selectiveCartEnabled
          ? cartLoaded && sflLoaded
          : cartLoaded
      )
    );
  }

  private getUserAccount(): void {
    this.isNetRestrictedPriceGroup$ = this.userAccount.get()
      .pipe(
        filter(user => !!user),
        map(user => user?.roles?.includes(GenUserGroup.netPricingRestrictedGroup))
      );
  }

  private sendGAViewCartEvent(): void {
    this.subscriptions.add(
      this.cart$.pipe(
        take(1),
        this.converterService.pipeable<Cart, Cart>(CART_NORMALIZER),
        tap((cart: Cart) => {
          if (cart?.entryGroups?.length > 0) {
            this.googleAnalyticsService.sendGaEvent('view_cart', {
              currency: cart.subTotal.currencyIso,
              value: cart.totalPriceWithTax.value,
              orderDiscount: cart?.promoDiscounts?.value < 0 ? Math.abs(cart?.promoDiscounts?.value) : undefined,
              coupon: cart?.appliedVouchers?.length > 0 ? cart?.appliedVouchers[0].voucherCode : undefined,
              items: this.googleAnalyticsService.buildGaItems(cart.entryGroups, cart?.appliedProductPromotions)
            });
          }
        })
      ).subscribe()
    )
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
